import {
  map,
  tap,
  pipe,
  take,
  EMPTY,
  switchMap,
  exhaustMap,
  distinctUntilChanged,
} from 'rxjs';
import {
  withProps,
  withMethods,
  signalStoreFeature,
  WritableStateSource,
} from '@ngrx/signals';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseAuthOutput } from './utils/firebase-auth';
import { mapFirebaseAuthErrorToMessage } from './utils/auth-error';
import { withCustomCallState } from '../utils/with-custom-call-state.feature';

type EmailVerificationResetDeps = WritableStateSource<
  FirebaseAuthOutput['state']
> &
  Pick<
    FirebaseAuthOutput['methods'],
    '_sendEmailVerification' | '_verifyEmail'
  > &
  Pick<FirebaseAuthOutput['props'], '_getUserSession$'>;

export function withEmailVerificationResources<
  T extends EmailVerificationResetDeps,
>(store: T) {
  return signalStoreFeature(
    withCustomCallState('emailVerification'),

    withProps((_, route = inject(ActivatedRoute)) => ({
      _router: inject(Router),
      _oobCode: toSignal(
        route.queryParamMap.pipe(
          map((params) => params.get('oobCode')),
          distinctUntilChanged(),
        ),
        { initialValue: null },
      ),
    })),

    withMethods((innerStore) => ({
      sendEmailVerification: rxMethod<void>(
        pipe(
          tap(() => innerStore._emailVerificationSetLoading()),
          exhaustMap(() =>
            store._getUserSession$.pipe(
              take(1),
              switchMap((user) => {
                if (!user) {
                  innerStore._emailVerificationSetError(
                    'Usuario actual faltante.',
                  );
                  return EMPTY;
                }

                return store._sendEmailVerification(user).pipe(
                  tapResponse({
                    next: () => innerStore._emailVerificationSetSuccess(),
                    error: (err: unknown) =>
                      innerStore._emailVerificationSetError(
                        mapFirebaseAuthErrorToMessage(err),
                      ),
                  }),
                );
              }),
            ),
          ),
        ),
      ),

      verifyEmailFromRoute: rxMethod<void>(
        pipe(
          tap(() => innerStore._emailVerificationSetLoading()),
          exhaustMap(() => {
            const code = innerStore._oobCode();

            if (!code) {
              innerStore._emailVerificationSetError(
                'Falta el código de verificación en el enlace.',
              );
              return EMPTY;
            }

            return store._verifyEmail(code).pipe(
              tapResponse({
                next: () => innerStore._emailVerificationSetSuccess(),
                error: (err: unknown) =>
                  innerStore._emailVerificationSetError(
                    mapFirebaseAuthErrorToMessage(err),
                  ),
              }),
            );
          }),
        ),
      ),

      goToDashboard: rxMethod<void>(
        pipe(exhaustMap(() => innerStore._router.navigate(['/dashboard']))),
      ),
    })),
  );
}
